import { AxiosInstance, AxiosResponse } from 'axios';

import { AuthenticationService } from './AuthenticationService';
import { DefaultRoutes } from 'Routes';
import { useAuthorizationStore } from 'stores/AuthorizationStore';

export class AxiosServiceConfig {
  static WaitingForToken: boolean = false;
  static setDefaultDecorators = (axiosInstance: AxiosInstance, authenticationService: AuthenticationService) => {
    const requestDefaultErrorHandler = (error: any) => {
      // Do something with request error
      return Promise.reject(error);
    };

    const responseDefaultErrorHandler = (error: any) => {
      const response = error.response;
      const config = error.config as any | undefined;
      const url: string | undefined = config?.url;

      const isReportEndpoint =
        typeof url === 'string' && url.indexOf('/api/v1/Report/') !== -1;

      // Retry once for Report endpoints on 504 Gateway Timeout
      if (response?.status === 504 && isReportEndpoint && config) {
        const currentRetry: number = config.__reportRetryCount || 0;
        const maxRetry = 1;

        if (currentRetry < maxRetry) {
          config.__reportRetryCount = currentRetry + 1;
          return axiosInstance(config);
        }
      }

      if (response?.status === 403 && window.location.pathname !== DefaultRoutes.Forbidden) {
        window.location.replace(DefaultRoutes.Forbidden);
      }

      return Promise.resolve(response);
    };

    const responseDefaultDecorator = (response: AxiosResponse<any>) => {
      if (response.headers['content-type'] && response.headers['content-type'].indexOf('application/json') > -1) {
        response.data = {
          isError: false,
          data: response.data,
        };
      }
      return response;
    };

    const requestDefaultDecorator = (request: any) => {
      return waitForToken().then(() => {
        return requestDecorator(request);
      });
    };

    const requestDecorator = (request: any) => {
      const originalRequest = request;
      const hasToken = authenticationService.hasToken();
      const authData = authenticationService.getAuthData();

      if (hasToken && authData) {
        // Add token before request is sent
        request.headers['Authorization'] = 'Bearer ' + authData.accessToken;
        AxiosServiceConfig.WaitingForToken = false;
        return request;
      }
      return authenticationService.getToken().then(() => {
        const hasToken = authenticationService.hasToken();
        const authData = authenticationService.getAuthData();
        AxiosServiceConfig.WaitingForToken = false;
        if (hasToken && authData) {
          originalRequest.headers['Authorization'] = 'Bearer ' + authData.accessToken;
          return Promise.resolve(originalRequest);
        }
        useAuthorizationStore.getState().setIsLoggedOutModalOn(true);
        return Promise.reject(originalRequest);
      });
    };

    const waitForToken = () => {
      return new Promise(function (resolve, reject) {
        (function checkToken() {
          if (!AxiosServiceConfig.WaitingForToken) {
            AxiosServiceConfig.WaitingForToken = true;
            return resolve(undefined);
          }
          setTimeout(checkToken, 20);
        })();
      });
    };

    // Add a request and response interceptor
    axiosInstance.interceptors.request.use(requestDefaultDecorator, requestDefaultErrorHandler);
    axiosInstance.interceptors.response.use(responseDefaultDecorator, responseDefaultErrorHandler);
  };
}
